import { APPLICATION_ID } from '../config';
import API from './API';
import {
  ApiResponse,
  PaginatedResponse,
  PackageDetails,
  PackageStep1Data,
  PackageStep2Data,
  PackageStep3Data,
  Plan,
  JobPosting,
  JobPostingSearchParams,
  JobPostingFormData,
  MilestoneFormData,
  InviteMember,
  UpdateTeamMemberRoleParams,
  SearchTeamMemberParams,
  ResetPasswordRequest,
  TeamInvitationResponse,
  KnowledgeHubArticle,
  KnowledgeHubListResponse,
  FreelancerBrowseParams,
  FreelancerPackageBrowseItem,
  FreelancerPackagePreview,
  PackageJoinRequest,
  CreateJoinRequestData,
  ReviewJoinRequestData,
  PendingCountResponse,
  PackageFreelancerSettings,
  UpdateFreelancerSettingsData,
  PlatformSettings,
  TimezoneOption,
  UpdatePlatformSettingsData,
} from '../types/api.types';
import type { CreateDisputeRequest, DisputeRecord, OrderEscrowRecord } from '../types/dispute.types';

export const userLogin = async (data: any) => {
  return await API({
    method: 'POST',
    url: 'user-auth/api/users/login',
    data: data,
  }).then((res) => {
    return res;
  });
};

export const userLoginWithGoogle = async (data: any) => {
  return await API({
    method: 'POST',
    url: 'user-auth/api/login/google',
    data: data,
  }).then((res) => {
    return res;
  });
};

export const userLoginWithLinkedin = async (code: any, applicationid: any) => {
  return await API({
    method: 'GET',
    url: `user-auth/api/login/linkedin/callback?code=${code}&state=${applicationid}`,
  }).then((res) => {
    return res;
  });
};
export const userRegister = async (data: any) => {
  return await API({
    method: 'POST',
    url: 'user-auth/api/users',
    data: data,
  }).then((res) => {
    return res;
  });
};
export const userForgotPassword = async (email: string, applicationId: string) => {
  return await API({
    method: 'POST',
    url: `user-auth/api/users/forgot-password?email=${email}&applicationId=${applicationId}`,
    data: null,
  }).then((res) => {
    return res;
  });
};
export const userVerifyOTP = async (email: string, applicationId: string, otp: string) => {
  return await API({
    method: 'POST',
    url: `user-auth/api/users/verify-otp?email=${email}&applicationId=${applicationId}&otp=${otp}`,
    data: null,
  }).then((res) => {
    return res;
  });
};
export const userResetPassword = async (
  email: string,
  applicationId: string,
  otp: string,
  newPassword: string,
) => {
  const requestData: ResetPasswordRequest = {
    email,
    applicationId,
    otp,
    newPassword,
  };

  return await API({
    method: 'POST',
    url: `user-auth/api/users/reset-password`,
    data: requestData,
  }).then((res) => {
    return res;
  });
};
export const userChangePassword = async (data: any) => {
  return await API({
    method: 'POST',
    url: `user-auth/api/users/change-password`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const requestEmailVerificationOTP = async (email: string) => {
  return await API({
    method: 'POST',
    url: `user-auth/api/users/request-email-verification?email=${email}&applicationId=${APPLICATION_ID}`,
    data: null,
  }).then((res) => {
    return res;
  });
};

export const resendEmailVerificationOTP = async (email: string) => {
  return await API({
    method: 'POST',
    url: `user-auth/api/users/resend-email-verification?email=${email}&applicationId=${APPLICATION_ID}`,
    data: null,
  }).then((res) => {
    return res;
  });
};

export const verifyEmailOTP = async (data: any) => {
  return await API({
    method: 'POST',
    url: `user-auth/api/users/verify-email`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const getUserDataByID = async (id: string) => {
  return await API({
    method: 'GET',
    url: `user-auth/api/users/${id}`,
  }).then((res) => {
    return res;
  });
};

export type ClientLookupResponse = {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  emailVerified?: boolean;
};

export const findClientByEmail = async (email: string): Promise<ApiResponse<ClientLookupResponse>> => {
  return await API({
    method: 'GET',
    url: `user-auth/api/users/clients/by-email/${encodeURIComponent(email)}`,
  });
};

export const findClientById = async (userId: string): Promise<ApiResponse<ClientLookupResponse>> => {
  return await API({
    method: 'GET',
    url: `user-auth/api/users/clients/by-id/${encodeURIComponent(userId.trim())}`,
  });
};

export const getFreelancerOnboardingDetails = async (id: string) => {
  return await API({
    method: 'GET',
    url: `user-profile/api/freelancer-onboarding/${id}`,
  }).then((res) => {
    return res;
  });
};

/** Public profile for creators — excludes personal details (address, phone, email, gov ID) */
export const getPublicProfile = async (userId: string) => {
  return await API({
    method: 'GET',
    url: `user-profile/api/public-profile/${userId}`,
  });
};

/** @deprecated use getPublicProfile */
export const getPublicFreelancerProfile = getPublicProfile;

export const saveFreelancerOnboardingDetails = async (data: any) => {
  return await API({
    method: 'POST',
    url: `user-profile/api/freelancer-onboarding/save-draft`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const submitFreelancerOnboardingDetails = async (data: any) => {
  return await API({
    method: 'POST',
    url: `user-profile/api/freelancer-onboarding/submit`,
    data: data,
  }).then((res) => {
    return res;
  });
};
export const submitCreatorOnboardingDetails = async (data: any) => {
  return await API({
    method: 'POST',
    url: `user-profile/api/creators/apply`,
    data: data,
  }).then((res) => {
    return res;
  });
};
export const creatorOnboardingStatus = async (id: string) => {
  return await API({
    method: 'GET',
    url: `user-profile/api/creators/applications/status/${id}`,
  }).then((res) => {
    return res;
  });
};
export const getCreatorOnboardingByUserId = async (id: string) => {
  return await API({
    method: 'GET',
    url: `user-profile/api/creators/applications/view/${id}`,
  }).then((res) => {
    return res;
  });
};

export const previewFreelancerOnboardingDetails = async (id: string, data: any) => {
  return await API({
    method: 'PUT',
    url: `user-profile/api/freelancer-onboarding/${id}`,
    data: data,
  }).then((res) => {
    return res;
  });
};
export const updateFreelancerOnboardingDetails = async (id: string, data: any) => {
  return await API({
    method: 'PUT',
    url: `user-profile/api/freelancer-onboarding/${id}`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const uploadFile = async (fileType: string, data: any) => {
  return await API({
    method: 'POST',
    url: `user-profile/api/blob/upload?type=${fileType}`,
    data: data,
    contentType: 'multipart/form-data',
  }).then((res) => {
    return res;
  });
};

export const getMetaData = async () => {
  return await API({
    method: 'GET',
    url: `user-profile/api/metadata`,
  }).then((res) => {
    return res;
  });
};

export const catalogFileUpload = async (fileType: string, data: any) => {
  return await API({
    method: 'POST',
    url: `catalog/api/blob/upload?type=${fileType}`,
    data: data,
    contentType: 'multipart/form-data',
  }).then((res) => {
    return res;
  });
};
// Get all packages with pagination
export const getAllPackages = async (
  page: number = 0,
  size: number = 20,
  sort: string = 'publishedAt,desc',
  category?: string,
  search?: string,
): Promise<ApiResponse<PaginatedResponse<PackageDetails>>> => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('size', String(size));
  params.append('sort', sort);
  if (category) params.append('category', category);
  if (search) params.append('search', search);
  return await API({
    method: 'GET',
    url: `catalog/api/packages?${params.toString()}`,
  });
};

// Get published/browse packages (public listing for all users)
export const getBrowsePackages = async (
  page: number = 0,
  size: number = 20,
): Promise<ApiResponse<PaginatedResponse<PackageDetails>>> => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('size', String(size));
  params.append('sort', 'desc');
  return await API({
    method: 'GET',
    url: `catalog/api/catalog/public/packages?${params.toString()}`,
  });
};

// Get single package by ID
export const getSinglePackage = async (id: string): Promise<ApiResponse<PackageDetails>> => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${id}`,
  });
};

// Create package - Step 1
export const createPackagesStep1 = async (
  data: Omit<PackageStep1Data, 'id' | 'draft'>,
): Promise<ApiResponse<PackageDetails>> => {
  return await API({
    method: 'POST',
    url: 'catalog/api/packages/step1',
    data: { ...data, draft: true, publish: false },
  });
};

// Create package - Step 2
export const createPackagesStep2 = async (
  data: Omit<PackageStep2Data, 'draft' | 'publish'>,
): Promise<ApiResponse<PackageDetails>> => {
  return await API({
    method: 'PUT',
    url: 'catalog/api/packages/step2',
    data: { ...data, draft: true },
  });
};

// Create package - Step 3
export const createPackagesStep3 = async (
  data: PackageStep3Data,
): Promise<ApiResponse<PackageDetails>> => {
  return await API({
    method: 'PUT',
    url: 'catalog/api/packages/step3',
    data,
  });
};

export const submitPackageForApproval = async (
  packageId: string,
): Promise<ApiResponse<PackageDetails>> => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${packageId}/submit-for-approval`,
    data: {},
  });
};

export type PackageApprovalHistoryEntry = {
  id: string;
  packageId: string;
  fromStatus: string | null;
  toStatus: string;
  feedback: string | null;
  eventType: string;
  changedBy: string;
  changedAt: string;
};

export const getPackageApprovalHistory = async (packageId: string): Promise<PackageApprovalHistoryEntry[]> => {
  const res = await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/approval-history`,
  });
  return res.data;
};

export type CreatorNotification = {
  id: string;
  type: string;
  title: string;
  message: string;
  entityType?: string | null;
  entityId?: string | null;
  packageId: string | null;
  linkPath: string | null;
  read: boolean;
  createdAt: string;
};

export const getCreatorNotifications = async (page = 0, size = 20) => {
  const res = await API({
    method: 'GET',
    url: `catalog/api/notifications?page=${page}&size=${size}`,
  });
  return res.data;
};

/** Hard-stop remount storms from hammering unread-count. */
const UNREAD_COUNT_TTL_MS = 15_000;
let unreadCountCache: { value: number; at: number } | null = null;
let unreadCountInFlight: Promise<number> | null = null;

const normalizeUnreadCount = (raw: unknown): number => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (raw && typeof raw === 'object' && 'count' in raw) {
    const nested = (raw as { count: unknown }).count;
    const n = typeof nested === 'number' ? nested : Number(nested);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

export const getCreatorUnreadNotificationCount = async (
  options?: { force?: boolean },
): Promise<number> => {
  if (options?.force) {
    unreadCountCache = null;
  }

  const now = Date.now();
  if (
    unreadCountCache &&
    now - unreadCountCache.at < UNREAD_COUNT_TTL_MS
  ) {
    return unreadCountCache.value;
  }

  // Coalesce concurrent callers (including remount storms).
  if (unreadCountInFlight) {
    return unreadCountInFlight;
  }

  unreadCountInFlight = API({
    method: 'GET',
    url: 'catalog/api/notifications/unread-count',
  })
    .then((res) => {
      const value = normalizeUnreadCount(res.data?.count ?? res.data);
      unreadCountCache = { value, at: Date.now() };
      return value;
    })
    .finally(() => {
      unreadCountInFlight = null;
    });

  return unreadCountInFlight;
};

export const invalidateCreatorUnreadNotificationCount = () => {
  unreadCountCache = null;
};

export const markCreatorNotificationRead = async (notificationId: string) => {
  return API({
    method: 'PUT',
    url: `catalog/api/notifications/${notificationId}/read`,
  });
};

export const markAllCreatorNotificationsRead = async () => {
  return API({
    method: 'PUT',
    url: 'catalog/api/notifications/read-all',
  });
};

// Update package - Step 1
export const updatePackagesStep1 = async (
  data: PackageStep1Data,
): Promise<ApiResponse<PackageDetails>> => {
  if (!data.id) {
    throw new Error('Package ID is required for update');
  }
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${data.id}/step1`,
    data,
  });
};

// Update package - Step 2
export const updatePackagesStep2 = async (
  data: PackageStep2Data,
): Promise<ApiResponse<PackageDetails>> => {
  if (!data.packageId) {
    throw new Error('Package ID is required for update');
  }
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${data.packageId}/step2`,
    data,
  });
};

/** Update a single package plan (plans live in a separate collection from the package document). */
export const updatePackagePlan = async (
  planId: string,
  data: {
    name: string;
    type: string;
    description: string;
    price: number;
    deliveryTime: number;
    deliveryUnit: string;
    revisions: number;
    addOns: Array<{
      name: string;
      price: number;
      enabled: boolean;
      description?: string;
      deliveryTime?: number;
      deliveryUnit?: string;
    }>;
    platformAddonCodes?: string[];
    displayOrder: number;
  },
): Promise<ApiResponse<Plan>> => {
  if (!planId) {
    throw new Error('Plan ID is required for update');
  }
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/plans/${planId}`,
    data,
  });
};

// Update package - Step 3
export const updatePackagesStep3 = async (
  data: PackageStep3Data,
): Promise<ApiResponse<PackageDetails>> => {
  if (!data.packageId) {
    throw new Error('Package ID is required for update');
  }
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${data.packageId}/step3`,
    data,
  });
};

// Update package - Step 3
export const updatePackage = async (data: PackageDetails): Promise<ApiResponse<PackageDetails>> => {
  if (!data.id) {
    throw new Error('Package ID is required for update');
  }
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${data.id}`,
    data,
  });
};

// Update package - Step 3
export const publishPackage = async (
  packageId: string,
): Promise<ApiResponse<PackageDetails>> => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${packageId}/publish`,
    data: {},
  });
};

// Get my packages
export const getMyPackages = async (): Promise<ApiResponse<PackageDetails[]>> => {
  return await API({
    method: 'GET',
    url: 'catalog/api/packages/my-packages',
  });
};

export type CreatorCoupon = {
  id: number;
  couponCode: string;
  couponType: 'CREATOR' | 'PLATFORM';
  creatorId: string | null;
  packageId: string | null;
  description: string;
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: number;
  maxDiscountAmount: number | null;
  minOrderAmount: number | null;
  usageLimit: number | null;
  usageCount: number;
  perUserLimit: number | null;
  validFrom: string | null;
  validUntil: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CreatorCouponRequest = {
  couponCode: string;
  packageId?: string | null;
  description: string;
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: number;
  maxDiscountAmount?: number | null;
  minOrderAmount?: number | null;
  usageLimit?: number | null;
  perUserLimit?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
};

export const getCreatorCoupons = async (
  page: number = 0,
  size: number = 50,
): Promise<{ data: { content: CreatorCoupon[] } }> => {
  return await API({
    method: 'GET',
    url: `order-management/api/v1/creator/coupons?page=${page}&size=${size}&sortBy=createdAt&sortDir=DESC`,
  });
};

export const createCreatorCoupon = async (data: CreatorCouponRequest) => {
  return await API({
    method: 'POST',
    url: 'order-management/api/v1/creator/coupons',
    data,
  });
};

export const updateCreatorCoupon = async (couponCode: string, data: CreatorCouponRequest) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/v1/creator/coupons/${couponCode}`,
    data,
  });
};

export const deactivateCreatorCoupon = async (couponCode: string) => {
  return await API({
    method: 'POST',
    url: `order-management/api/v1/creator/coupons/${couponCode}/deactivate`,
  });
};

export type UserPlatformPlanResponse = {
  id: number;
  userId: string;
  plan: {
    id: number;
    planCode: string;
    planName: string;
    platformFeePercentage: number;
  };
};

export type PlatformAddon = {
  id: number;
  addonCode: string;
  addonName: string;
  description: string | null;
  addonType: 'MARKETING' | 'ADVERTISING' | 'SALES_ENABLEMENT' | 'CUSTOM';
  chargeType: 'FIXED' | 'PERCENTAGE';
  chargeValue: number;
  minChargeAmount?: number | null;
  maxChargeAmount?: number | null;
  currency: string;
  isActive: boolean;
};

export const getUserCurrentPlatformPlan = async (userId: string): Promise<ApiResponse<UserPlatformPlanResponse>> => {
  return await API({
    method: 'GET',
    url: `order-management/api/v1/admin/plans/users/${userId}`,
  });
};

export const getActivePlatformAddons = async (): Promise<ApiResponse<PlatformAddon[]>> => {
  return await API({
    method: 'GET',
    url: 'order-management/api/v1/admin/platform-addons/active',
  });
};

export const getAllGigsData = async () => {
  return await API({
    method: 'GET',
    url: `catalog/api/gigs/all`,
  }).then((res) => {
    return res;
  });
};

export const getSingleGigsData = async (id: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/gigs/${id}`,
  }).then((res) => {
    return res;
  });
};

export const getMyGigsData = async () => {
  return await API({
    method: 'GET',
    url: `catalog/api/gigs/me`,
  }).then((res) => {
    return res;
  });
};
export const createGigsData = async (data: any) => {
  return await API({
    method: 'POST',
    url: `catalog/api/gigs`,
    data: data,
  }).then((res) => {
    return res;
  });
};
export const updateGigsData = async (data: any) => {
  return await API({
    method: 'PUT',
    url: `catalog/api/gigs/${data.id}`,
    data: data,
  }).then((res) => {
    return res;
  });
};

// Job Postings
// APIs for creating and managing job postings for packages

/** Backend uses `subcategory`; form state uses `subCategory`. */
const toJobPostingApiPayload = (data: JobPostingFormData) => {
  const { subCategory, ...rest } = data;
  return {
    ...rest,
    subcategory: subCategory ?? (data as JobPostingFormData & { subcategory?: string[] }).subcategory,
  };
};

const normalizeJobPosting = (job: JobPosting & { subcategory?: string[] }): JobPosting => ({
  ...job,
  category: job.category ?? [],
  subCategory: job.subCategory ?? job.subcategory ?? [],
});

export const getJobPostingByPackage = async (packageId: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/job-postings`,
  }).then((res) => {
    if (Array.isArray(res.data)) {
      res.data = res.data.map((job: JobPosting & { subcategory?: string[] }) => normalizeJobPosting(job));
    }
    return res;
  });
};
export const getJobPostingById = async (id: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/job-postings/${id}`,
  }).then((res) => {
    if (res.data) {
      res.data = normalizeJobPosting(res.data);
    }
    return res;
  });
};
export const getMyJobPostings = async () => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/job-postings/my-postings`,
  }).then((res) => {
    if (Array.isArray(res.data)) {
      res.data = res.data.map((job: JobPosting & { subcategory?: string[] }) => normalizeJobPosting(job));
    }
    return res;
  });
};
export const getMyActiveJobPostings = async () => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/job-postings/my-active-postings`,
  }).then((res) => {
    if (Array.isArray(res.data)) {
      res.data = res.data.map((job: JobPosting & { subcategory?: string[] }) => normalizeJobPosting(job));
    }
    return res;
  });
};

export const searchJobPostings = async (params: JobPostingSearchParams) => {
  // Convert array parameters to query string format
  const queryParams = new URLSearchParams();

  // Add all parameters to query
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      // Handle array parameters (like skills)
      value.forEach((item) => queryParams.append(key, item));
    } else {
      queryParams.append(key, String(value));
    }
  });

  return await API({
    method: 'GET',
    url: `catalog/api/packages/job-postings/search?${queryParams.toString()}`,
  }).then((res) => {
    return res;
  });
};

export const createJobPosting = async (data: JobPostingFormData) => {
  return await API({
    method: 'POST',
    url: `catalog/api/packages/job-postings`,
    data: toJobPostingApiPayload(data),
  }).then((res) => {
    if (res.data) {
      res.data = normalizeJobPosting(res.data);
    }
    return res;
  });
};

export const updateJobPosting = async (data: JobPostingFormData, id: string) => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/job-postings/${id}`,
    data: toJobPostingApiPayload(data),
  }).then((res) => {
    if (res.data) {
      res.data = normalizeJobPosting(res.data);
    }
    return res;
  });
};

export const publishJobPosting = async (id: string) => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/job-postings/${id}/publish`,
    data: {},
  }).then((res) => {
    if (res.data) {
      res.data = normalizeJobPosting(res.data);
    }
    return res;
  });
};

export const closeJobPosting = async (id: string) => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/job-postings/${id}/close`,
    data: {},
  }).then((res) => {
    if (res.data) {
      res.data = normalizeJobPosting(res.data);
    }
    return res;
  });
};

export const updateJobPostingFreelancerVisibility = async (
  id: string,
  openForFreelancers: boolean,
) => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/job-postings/${id}/open-for-freelancers`,
    data: { openForFreelancers },
  }).then((res) => {
    if (res.data) {
      res.data = normalizeJobPosting(res.data);
    }
    return res;
  });
};

export const deleteJobPosting = async (id: string) => {
  return await API({
    method: 'DELETE',
    url: `catalog/api/packages/job-postings/${id}`,
    data: {},
  }).then((res) => {
    return res;
  });
};

// Join Package / Freelancer
export const browseFreelancerPackages = async (
  params: FreelancerBrowseParams,
): Promise<ApiResponse<PaginatedResponse<FreelancerPackageBrowseItem>>> => {
  const queryParams = new URLSearchParams();
  if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
  if (params.category) queryParams.append('category', params.category);
  if (params.industry) queryParams.append('industry', params.industry);
  if (params.country) queryParams.append('country', params.country);
  if (params.page !== undefined) queryParams.append('page', String(params.page));
  if (params.size !== undefined) queryParams.append('size', String(params.size));
  return await API({
    method: 'GET',
    url: `catalog/api/packages/freelancer/browse?${queryParams.toString()}`,
  });
};

export const getFreelancerPackagePreview = async (
  packageId: string,
): Promise<ApiResponse<FreelancerPackagePreview>> => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/freelancer/${packageId}/preview`,
  });
};

export const createPackageJoinRequest = async (
  packageId: string,
  data: CreateJoinRequestData,
): Promise<ApiResponse<PackageJoinRequest>> => {
  return await API({
    method: 'POST',
    url: `catalog/api/packages/${packageId}/join-requests`,
    data,
  });
};

export const getPackageJoinRequestStatus = async (
  packageId: string,
): Promise<ApiResponse<PackageJoinRequest>> => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/join-requests/status`,
  });
};

export const getMyJoinRequests = async (): Promise<ApiResponse<PackageJoinRequest[]>> => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/join-requests/my-requests`,
  });
};

export const withdrawJoinRequest = async (requestId: string) => {
  return await API({
    method: 'DELETE',
    url: `catalog/api/packages/join-requests/${requestId}`,
  }).then((res) => res);
};

export const getPackageJoinRequests = async (
  packageId: string,
  status?: string,
): Promise<ApiResponse<PackageJoinRequest[]>> => {
  const statusParam = status && status !== 'ALL' ? `?status=${status}` : '';
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/join-requests${statusParam}`,
  });
};

export const getPackageJoinRequestsPaged = async (
  packageId: string,
  page: number = 0,
  size: number = 10,
): Promise<ApiResponse<PaginatedResponse<PackageJoinRequest>>> => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/join-requests/paged?page=${page}&size=${size}`,
  });
};

export const getPackageJoinRequestsPendingCount = async (
  packageId: string,
): Promise<ApiResponse<PendingCountResponse>> => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/join-requests/pending-count`,
  });
};

export const listPackageReviews = async (packageId: string, page = 0, size = 20) =>
  API({
    method: 'GET',
    url: `catalog/api/catalog/public/packages/${packageId}/reviews?page=${page}&size=${size}`,
  });

export const replyToPackageReview = async (
  packageId: string,
  reviewId: string,
  reply: string,
) =>
  API({
    method: 'POST',
    url: `catalog/api/catalog/packages/${packageId}/reviews/${reviewId}/reply`,
    data: { reply },
  });

export const reviewJoinRequest = async (
  requestId: string,
  data: ReviewJoinRequestData,
): Promise<ApiResponse<PackageJoinRequest>> => {
  return await API({
    method: 'POST',
    url: `catalog/api/packages/join-requests/${requestId}/review`,
    data,
  });
};

export const getPackageFreelancerSettings = async (
  packageId: string,
): Promise<ApiResponse<PackageFreelancerSettings>> => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/freelancer-settings`,
  });
};

export const updatePackageFreelancerSettings = async (
  packageId: string,
  data: UpdateFreelancerSettingsData,
): Promise<ApiResponse<PackageFreelancerSettings>> => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${packageId}/freelancer-settings`,
    data,
  });
};

export const updatePackageClientActive = async (
  packageId: string,
  active: boolean,
): Promise<ApiResponse<PackageDetails>> => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${packageId}/client-active`,
    data: { active },
  });
};

export const getMilestoneByPackage = async (packageId: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/milestones`,
  }).then((res) => {
    return res;
  });
};

export const getMilestoneByPlan = async (planId: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/plans/${planId}/milestones`,
  }).then((res) => {
    return res;
  });
};

export const getEligibilityMilestone = async (planId: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/plans/${planId}/milestones/payment-eligible`,
  }).then((res) => {
    return res;
  });
};

export const createMilestone = async (data: MilestoneFormData) => {
  return await API({
    method: 'POST',
    url: `catalog/api/packages/milestones/plan`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const updateMilestone = async (data: MilestoneFormData, id: string) => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/milestones/${id}`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const updateMilestoneStatus = async (id: string, status: string) => {
  return await API({
    method: 'PUT',
    url: `catalog/api/packages/milestones/${id}/status?status=${status}`,
    data: {},
  }).then((res) => {
    return res;
  });
};
export const deleteMilestone = async (id: string) => {
  return await API({
    method: 'DELETE',
    url: `catalog/api/packages/milestones/${id}`,
    data: {},
  }).then((res) => {
    return res;
  });
};

export const getTeamMemberByPackage = async (packageId: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/team/members`,
  }).then((res) => {
    return res;
  });
};
export const getTeamActiveMembers = async (packageId: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/team/active-members`,
  }).then((res) => {
    return res;
  });
};
export const getMyTeamMemberships = async () => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/team/my-memberships`,
  }).then((res) => {
    return res;
  });
};
export const getAllMyTeamInvitation = async () => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/team/my-memberships`,
  }).then((res) => {
    return res;
  });
};

export const inviteUserToTeam = async (data: InviteMember) => {
  return await API({
    method: 'POST',
    url: `catalog/api/packages/team/invite`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const invitedUserRespond = async (data: any) => {
  return await API({
    method: 'POST',
    url: `catalog/api/packages/team/invitation/respond`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const updateTeamMemberRole = async ({
  packageId,
  memberId,
  role,
  permissions = [], // Default to empty array if not provided
}: UpdateTeamMemberRoleParams) => {
  const params = new URLSearchParams();
  params.append('role', role);
  permissions.forEach((permission) => params.append('permissions', permission));

  return await API({
    method: 'PUT',
    url: `catalog/api/packages/${packageId}/team/members/${memberId}/role?${params.toString()}`,
  }).then((res) => {
    return res;
  });
};

export const removeTeamMember = async (packageId: string, memberId: string) => {
  return await API({
    method: 'DELETE',
    url: `catalog/api/packages/${packageId}/team/members/${memberId}`,
  }).then((res) => {
    return res;
  });
};

export const searchTeamMembers = async (packageId: string, searchParams: SearchTeamMemberParams) => {
  const params = new URLSearchParams();

  // Add all search parameters to the URL
  if (searchParams.searchTerm) params.append('searchTerm', searchParams.searchTerm);
  if (searchParams.skills && searchParams.skills.length > 0) {
    searchParams.skills.forEach(skill => params.append('skills', skill));
  }
  if (searchParams.location) params.append('location', searchParams.location);
  if (searchParams.experienceLevel) params.append('experienceLevel', searchParams.experienceLevel);
  if (searchParams.minHourlyRate !== undefined) params.append('minHourlyRate', searchParams.minHourlyRate.toString());
  if (searchParams.maxHourlyRate !== undefined) params.append('maxHourlyRate', searchParams.maxHourlyRate.toString());
  if (searchParams.availabilityStatus) params.append('availabilityStatus', searchParams.availabilityStatus);
  if (searchParams.userType) params.append('userType', searchParams.userType);
  if (searchParams.page !== undefined) params.append('page', searchParams.page.toString());
  if (searchParams.size !== undefined) params.append('size', searchParams.size.toString());
  if (searchParams.sortBy) params.append('sortBy', searchParams.sortBy);
  if (searchParams.sortDir) params.append('sortDir', searchParams.sortDir);

  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/team/search-users?${params.toString()}`,
  }).then((res) => {
    return res;
  });
}

export const getPackageTeamMembers = async (packageId: string) => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/${packageId}/team/members`,
  }).then((res) => {
    return res;
  });
};

export const updateProjectTeamMember = async (projectId: number, teamMemberId: number, data: any) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/projects/${projectId}/team-members/${teamMemberId}`,
    data: data,
  }).then((res) => {
    return res;
  });
};

export const deleteProjectTeamMember = async (projectId: number, teamMemberId: number) => {
  return await API({
    method: 'DELETE',
    url: `order-management/api/projects/${projectId}/team-members/${teamMemberId}`,
  }).then((res) => {
    return res;
  });
};

export const getProjectNotes = async (projectId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/projects/${projectId}/notes`,
  }).then((res) => {
    return res;
  });
};

export const addProjectNote = async (projectId: number, content: string) => {
  return await API({
    method: 'POST',
    url: `order-management/api/projects/${projectId}/notes`,
    data: { content },
  }).then((res) => {
    return res;
  });
};

// Get all my team invitations
export const getAllMyTeamInvitations = async () => {
  return await API({
    method: 'GET',
    url: `catalog/api/packages/team/my-memberships/all`,
  }).then((res) => {
    return res;
  });
};

// Respond to team invitation (Accept/Decline)
export const respondToTeamInvitation = async (invitationToken: string, response: 'ACCEPT' | 'DECLINE', responseMessage?: string) => {
  const requestData: TeamInvitationResponse = {
    invitationToken,
    response,
    responseMessage: responseMessage || ''
  };

  return await API({
    method: 'POST',
    url: `catalog/api/packages/team/invitation/respond`,
    data: requestData
  }).then((res) => {
    return res;
  });
};

// Accept team invitation (wrapper for convenience)
export const acceptTeamInvitation = async (invitationToken: string, responseMessage?: string) => {
  return respondToTeamInvitation(invitationToken, 'ACCEPT', responseMessage);
};

// Reject team invitation (wrapper for convenience)
export const rejectTeamInvitation = async (invitationToken: string, responseMessage?: string) => {
  return respondToTeamInvitation(invitationToken, 'DECLINE', responseMessage);
};

// Get my orders (for creators)
export const getMyOrders = async (page: number = 0, size: number = 10, sort: string = 'createdAt,desc', status?: string) => {
  const statusParam = status && status !== 'ALL' ? `&status=${status}` : '';
  return await API({
    method: 'GET',
    url: `order-management/api/orders/my-orders?page=${page}&size=${size}&sort=${sort}${statusParam}`,
  });
};


// Get order details by ID (for creators)
export const getOrderDetails = async (orderId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/orders/creator/${orderId}`,
  });
};


// Accept order (for creators)
export const acceptOrder = async (orderNumber: string, targetProjectId?: number | null) => {
  return await API({
    method: 'POST',
    url: `order-management/api/orders/${orderNumber}/accept`,
    data: targetProjectId ? { targetProjectId } : {},
  });
};

export type CustomQuotationMilestoneRequest = {
  title: string;
  description?: string;
  amount: number;
  dueDate: string;
};

export type CreateCustomQuotationRequest = {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  serviceTitle: string;
  serviceDescription?: string;
  packageId: string;
  planId?: string;
  totalAmount: number;
  currency: string;
  estimatedDeliveryDate: string;
  milestones: CustomQuotationMilestoneRequest[];
  requirements?: string;
  notes?: string;
  terms?: string;
  validityDays?: number;
};

export type CustomInvoiceMilestoneRequest = CustomQuotationMilestoneRequest;
export type CreateCustomInvoiceRequest = CreateCustomQuotationRequest;

export const createCustomQuotation = async (data: CreateCustomQuotationRequest) => {
  return await API({
    method: 'POST',
    url: 'order-management/api/orders/custom',
    data,
  });
};

export const updateCustomQuotation = async (orderId: number, data: CreateCustomQuotationRequest) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/orders/custom/${orderId}`,
    data,
  });
};

export const createCustomInvoice = createCustomQuotation;

// Start/Execute project
export const startProject = async (orderId: number, projectData: {
  projectName: string;
  startDate: string;
  expectedEndDate: string;
  initialNotes: string;
  startDateValid: boolean;
  endDateValid: boolean;
}) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/orders/projects/${orderId}/start`,
    data: projectData,
  });
};

// Get my projects (for creators)
export const getMyProjects = async (page: number = 0, size: number = 10) => {
  return await API({
    method: 'GET',
    url: `order-management/api/projects/my-projects?page=${page}&size=${size}`,
  });
};

// Get my team projects (for freelancers)
export const getMyTeamProjects = async (page: number = 0, size: number = 10) => {
  return await API({
    method: 'GET',
    url: `order-management/api/projects/my-team-projects?page=${page}&size=${size}`,
  });
};

// Get my tasks (for freelancers)
export const getMyTasks = async () => {
  return await API({
    method: 'GET',
    url: `order-management/api/tasks/my-tasks`,
  });
};

// Get overdue tasks
export const getOverdueTasks = async () => {
  return await API({
    method: 'GET',
    url: `order-management/api/tasks/overdue`,
  });
};

// Get milestone details
export const getMilestoneDetails = async (milestoneId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/milestones/${milestoneId}`,
  });
};

// Get task details by ID
export const getTaskDetails = async (taskId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/tasks/${taskId}`,
  });
};

// Get task comments
export const getTaskComments = async (taskId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/tasks/${taskId}/comments`,
  });
};

// Get task attachments
export const getTaskAttachments = async (taskId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/tasks/${taskId}/attachments`,
  });
};

// Upload task attachment
export const uploadTaskAttachment = async (
  taskId: number,
  file: File,
  attachmentType: string,
  description?: string,
  isDeliverable: boolean = false,
) => {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  params.append('attachmentType', attachmentType);
  params.append('isDeliverable', String(isDeliverable));
  if (description) {
    params.append('description', description);
  }

  return await API({
    method: 'POST',
    url: `order-management/api/tasks/${taskId}/attachments?${params.toString()}`,
    data: formData,
    contentType: 'multipart/form-data',
  });
};

// Post task comment
export const postTaskComment = async (taskId: number, commentData: {
  comment: string;
  commentType: string;
  isInternal: boolean;
  mentionedUsers?: string;
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/tasks/${taskId}/comments`,
    data: commentData,
  });
};

// Update task status
export const updateTaskStatus = async (taskId: number, status: string) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/tasks/${taskId}/status?status=${status}`,
  });
};

// Get project details by ID
export const getProjectDetails = async (projectId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/projects/${projectId}`,
  });
};

// Get project tasks
export const getProjectTasks = async (projectId: number, assignedToId?: string) => {
  const assignedToParam = assignedToId ? `?assignedToId=${encodeURIComponent(assignedToId)}` : '';
  return await API({
    method: 'GET',
    url: assignedToId
      ? `order-management/api/projects/${projectId}/tasks/filter${assignedToParam}`
      : `order-management/api/projects/${projectId}/tasks`,
  });
};

// Add task to milestone
export const addTask = async (milestoneId: number, taskData: {
  name: string;
  description: string;
  amount: number;
  deadline: string;
  assignedToId: string;
  assignedToName: string;
  deliverables: string;
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/milestones/${milestoneId}/tasks`,
    data: taskData,
  });
};

// Update task
export const updateTask = async (taskId: number, taskData: {
  name: string;
  description: string;
  amount: number;
  deadline: string;
  assignedToId: string;
  assignedToName: string;
  deliverables: string;
  status: string;
}) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/tasks/${taskId}`,
    data: taskData,
  });
};

// Delete task
export const deleteTask = async (taskId: number) => {
  return await API({
    method: 'DELETE',
    url: `order-management/api/tasks/${taskId}`,
  });
};

export const createTeamPaymentDistribution = async (data: {
  orderId: string;
  taskId: number;
  contributorId: string;
  amount: number;
  assignmentNotes?: string;
}) => {
  return await API({
    method: 'POST',
    url: 'order-management/api/v1/creator/payments/distribute',
    data,
  });
};

export const approveTeamPaymentDistribution = async (distributionId: string) => {
  return await API({
    method: 'POST',
    url: `order-management/api/v1/creator/payments/distributions/${distributionId}/approve`,
  });
};

// Get project discussions
export const getProjectDiscussions = async (projectId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/projects/${projectId}/discussions`,
  });
};

// Add project discussion
export const addProjectDiscussion = async (projectId: number, discussionData: {
  content: string;
  parentId?: number;
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/projects/${projectId}/discussions`,
    data: discussionData,
  });
};

// Add project milestone
export const addProjectMilestone = async (projectId: number, milestoneData: {
  title: string;
  description?: string;
  budgetAllocation: number;
  currency?: string;
  startDate?: string;
  dueDate: string;
  sequenceOrder?: number;
  deliverables?: string;
  acceptanceCriteria?: string;
  notes?: string;
  assignedTo?: string;
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/projects/${projectId}/milestones`,
    data: milestoneData,
  });
}

// Update project milestone
export const updateProjectMilestone = async (projectId: number, milestoneId: number, milestoneData: {
  title: string;
  description?: string;
  budgetAllocation: number;
  currency?: string;
  startDate?: string;
  dueDate: string;
  sequenceOrder?: number;
  deliverables?: string;
  acceptanceCriteria?: string;
  notes?: string;
  assignedTo?: string;
}) => {
  console.log('updateProjectMilestone called with:', { projectId, milestoneId, milestoneData });
  
  return await API({
    method: 'PUT',
    url: `order-management/api/projects/${projectId}/milestones/${milestoneId}`,
    data: milestoneData,
  }).then((res) => {
    console.log('updateProjectMilestone response:', res);
    return res;
  }).catch((err) => {
    console.error('updateProjectMilestone error:', err);
    throw err;
  });
}

// Delete project milestone
export const deleteProjectMilestone = async (projectId: number, milestoneId: number) => {
  return await API({
    method: 'DELETE',
    url: `order-management/api/projects/${projectId}/milestones/${milestoneId}`,
  });
}

export const completeProjectMilestone = async (projectId: number, milestoneId: number, deliverableData: {
  title?: string;
  url?: string;
  description?: string;
  files?: File[];
}) => {
  const formData = new FormData();
  if (deliverableData.title) formData.append('title', deliverableData.title);
  if (deliverableData.url) formData.append('url', deliverableData.url);
  if (deliverableData.description) formData.append('description', deliverableData.description);
  (deliverableData.files || []).forEach((file) => {
    formData.append('files', file);
  });

  return await API({
    method: 'POST',
    url: `order-management/api/projects/${projectId}/milestones/${milestoneId}/complete`,
    data: formData,
    contentType: 'multipart/form-data',
  });
};

// Get project milestones
export const getProjectMilestones = async (projectId: number, includeTasks: boolean = false) => {
  return await API({
    method: 'GET',
    url: `order-management/api/projects/${projectId}/milestones?includeTasks=${includeTasks}`,
  });
};

// Add team member to project
export const addProjectTeamMember = async (projectId: number, teamMemberData: {
  userId: string;
  userName: string;
  role: string;
  responsibilities: string;
  joinedDate: string;
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/projects/${projectId}/team-members`,
    data: teamMemberData,
  });
};

// Get order requirements
export const getOrderRequirements = async (orderId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/orders/${orderId}/requirements`,
  });
};

// Add order requirement
export const addOrderRequirement = async (orderId: number, requirementData: {
  requirementType: 'TEXT' | 'FILE' | 'LINK';
  content: string;
  fileId?: number;
  title: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/orders/${orderId}/requirements`,
    data: requirementData,
  });
};

// Update order requirement
export const updateOrderRequirement = async (orderId: number, requirementId: number, requirementData: {
  requirementType: 'TEXT' | 'FILE' | 'LINK';
  content: string;
  fileId?: number;
  title: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
}) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/orders/${orderId}/requirements/${requirementId}`,
    data: requirementData,
  });
};

// Delete order requirement
export const deleteOrderRequirement = async (orderId: number, requirementId: number) => {
  return await API({
    method: 'DELETE',
    url: `order-management/api/orders/${orderId}/requirements/${requirementId}`,
  });
};

// Get order discussions
export const getOrderDiscussions = async (orderId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/orders/${orderId}/discussions`,
  });
};

// Add order discussion
export const addOrderDiscussion = async (orderId: number, discussionData: {
  content: string;
  parentId?: number;
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/orders/${orderId}/discussions`,
    data: discussionData,
  });
};
// Update order discussion
export const updateOrderDiscussion = async (orderId: number, discussionId: number, discussionData: {
  content: string;
}) => {
  return await API({
    method: 'PUT',
    url: `order-management/api/orders/${orderId}/discussions/${discussionId}?content=${encodeURIComponent(discussionData.content)}`,
  });
};

// Delete order discussion
export const deleteOrderDiscussion = async (orderId: number, discussionId: number) => {
  return await API({
    method: 'DELETE',
    url: `order-management/api/orders/${orderId}/discussions/${discussionId}`,
  });
};
// Upload file to order
export const uploadOrderFile = async (orderId: number, file: File, attachmentType: string, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  
  let url = `order-management/api/orders/${orderId}/files?attachmentType=${attachmentType}`;
  if (description) {
    url += `&description=${encodeURIComponent(description)}`;
  }
  
  return await API({
    method: 'POST',
    url: url,
    data: formData,
    contentType: 'multipart/form-data'
    // Don't set Content-Type header - let browser set it automatically with boundary
  });
};
// Get order files by type
export const getOrderFiles = async (orderId: number, type?: string) => {
  let url = `order-management/api/orders/${orderId}/files`;
  if (type) {
    url += `?type=${type}`;
  }
  
  return await API({
    method: 'GET',
    url: url,
  });
};
// Get order activities with pagination
export const getOrderActivities = async (orderId: number, page: number = 0, size: number = 20, sortDir: string = 'desc') => {
  return await API({
    method: 'GET',
    url: `order-management/api/orders/${orderId}/activities/paginated?page=${page}&size=${size}&sortDir=${sortDir}`,
  });
};
// Get order deliverable links
export const getOrderDeliverables = async (orderId: number) => {
  return await API({
    method: 'GET',
    url: `order-management/api/orders/${orderId}/deliverable-links`,
  });
};

// Add order deliverable link
export const addOrderDeliverable = async (orderId: number, deliverableData: {
  milestoneId: number;
  url: string;
  title: string;
  description: string;
}) => {
  return await API({
    method: 'POST',
    url: `order-management/api/orders/${orderId}/deliverable-links`,
    data: deliverableData,
  });
};
// Delete order deliverable link
export const deleteOrderDeliverable = async (orderId: number, deliverableId: number) => {
  return await API({
    method: 'DELETE',
    url: `order-management/api/orders/${orderId}/deliverable-links/${deliverableId}`,
  });
};
// Delete order file
export const deleteOrderFile = async (orderId: number, fileId: number) => {
  return await API({
    method: 'DELETE',
    url: `order-management/api/orders/${orderId}/files/${fileId}`,
  });
};


// ── Knowledge Hub ─────────────────────────────────────────────────────────────

// Get paginated list of knowledge hub articles
export const getKnowledgeHubArticles = async (
  page: number = 0,
  size: number = 20,
): Promise<ApiResponse<KnowledgeHubListResponse>> => {
  return await API({
    method: 'GET',
    url: `content/api/knowledge-hub/articles?page=${page}&size=${size}&sort=publishedAt,desc`,
  });
};

// Get a single knowledge hub article by ID
export const getKnowledgeHubArticleById = async (
  id: string,
): Promise<ApiResponse<KnowledgeHubArticle>> => {
  return await API({
    method: 'GET',
    url: `content/api/knowledge-hub/articles/${id}`,
  });
};

// Platform settings (timezone for date display)
export const getPlatformSettings = async (): Promise<ApiResponse<PlatformSettings>> => {
  return await API({
    method: 'GET',
    url: 'catalog/api/platform/settings',
  });
};

export const getPlatformTimezones = async (): Promise<ApiResponse<TimezoneOption[]>> => {
  return await API({
    method: 'GET',
    url: 'catalog/api/platform/settings/timezones',
  });
};

export const updatePlatformSettings = async (
  data: UpdatePlatformSettingsData,
): Promise<ApiResponse<PlatformSettings>> => {
  return await API({
    method: 'PUT',
    url: 'catalog/api/platform/settings',
    data: {
      timezone: data.timezone,
      portalType: data.portalType ?? 'FREELANCER',
    },
  });
};

export const getOrderEscrows = async (orderId: string | number) =>
  API({
    method: 'GET',
    url: `order-management/api/v1/finance/escrow/order/${orderId}`,
  });

export const createDispute = async (data: CreateDisputeRequest) =>
  API({
    method: 'POST',
    url: 'order-management/api/v1/disputes',
    data,
  });

export const getDisputesByOrder = async (orderId: string | number) =>
  API({
    method: 'GET',
    url: `order-management/api/v1/disputes/order/${orderId}`,
  });

export type ChatRoom = {
  id: string;
  type?: 'PACKAGE_INQUIRY' | 'CREATOR_FREELANCER';
  packageId: string;
  packageTitle: string;
  clientUserId?: string | null;
  clientDisplayId?: string | null;
  creatorUserId: string;
  freelancerUserId?: string | null;
  counterpartLabel?: string | null;
  status?: 'OPEN' | 'CONVERTED' | 'CLOSED';
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  unreadCount: number;
  hasUnread?: boolean;
  counterpartOnline?: boolean | null;
  counterpartLastSeenAt?: string | null;
};

export type ChatMessage = {
  id: string;
  roomId: string;
  senderUserId: string;
  body: string;
  createdAt: string;
  mine: boolean;
};

export type ChatNegotiateResponse = {
  url: string;
  group: string;
  hub: string;
  expiresAt: string;
};

type ChatEnvelope<T> = { status: number; message: string; data: T };

const chatPrefix = 'user-profile/api/chat';

export const createCreatorFreelancerChatRoom = async (packageId: string, freelancerUserId: string) =>
  API({
    method: 'POST',
    url: `${chatPrefix}/rooms/creator-freelancer`,
    data: { packageId, freelancerUserId },
  }).then((res) => (res.data as ChatEnvelope<ChatRoom>).data);

export const listChatRooms = async (page = 0, size = 20) =>
  API({
    method: 'GET',
    url: `${chatPrefix}/rooms?page=${page}&size=${size}`,
  }).then((res) => (res.data as ChatEnvelope<{ rooms: ChatRoom[] }>).data);

export const listChatMessages = async (roomId: string, page = 0, size = 50) =>
  API({
    method: 'GET',
    url: `${chatPrefix}/rooms/${roomId}/messages?page=${page}&size=${size}`,
  }).then((res) => (res.data as ChatEnvelope<{ messages: ChatMessage[]; hasMore: boolean }>).data);

export const sendChatMessage = async (roomId: string, body: string) =>
  API({
    method: 'POST',
    url: `${chatPrefix}/rooms/${roomId}/messages`,
    data: { body },
  }).then((res) => (res.data as ChatEnvelope<ChatMessage>).data);

export const markChatRoomRead = async (roomId: string) =>
  API({
    method: 'POST',
    url: `${chatPrefix}/rooms/${roomId}/read`,
  });

export const negotiateChatRoom = async (roomId: string) =>
  API({
    method: 'POST',
    url: `${chatPrefix}/rooms/${roomId}/negotiate`,
  }).then((res) => (res.data as ChatEnvelope<ChatNegotiateResponse>).data);

export const getChatUnreadCount = async () =>
  API({
    method: 'GET',
    url: `${chatPrefix}/unread-count`,
  }).then((res) => (res.data as ChatEnvelope<{ unreadRoomCount: number }>).data.unreadRoomCount);

export const sendChatPresenceHeartbeat = async () =>
  API({
    method: 'POST',
    url: `${chatPrefix}/presence/heartbeat`,
  });

export type { DisputeRecord, OrderEscrowRecord };
